import { Fragment, useEffect, useRef, type CSSProperties, type ReactNode } from 'react';
import { ChevronLeft, ChevronRight, Undo2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { getPreferredScrollBehavior } from '@/utils/motion';
import { UNLOCATED_STOP_COLOR } from '../constants/moTravel.constants';
import type { JourneyEntry } from '../utils/journeyEntries';
import { MoEndIcon } from './MoEndIcon';
import { MoStartIcon } from './MoStartIcon';

const STEP_KEY_ATTRIBUTE = 'data-step-key';
const ACTIVE_STEP_HALO_PX = 3;
const ACTIVE_STEP_HALO_MIX_PERCENT = 22;

function centerStepInTrack(track: HTMLElement, step: HTMLElement) {
  const trackRect = track.getBoundingClientRect();
  const stepRect = step.getBoundingClientRect();
  const trackCenterX = trackRect.left + trackRect.width / 2;
  const stepCenterX = stepRect.left + stepRect.width / 2;
  track.scrollBy({ left: stepCenterX - trackCenterX, behavior: getPreferredScrollBehavior() });
}

const getStepColor = (entry: JourneyEntry): string =>
  entry.kind === 'unlocated' ? UNLOCATED_STOP_COLOR : entry.dayColor;

function renderStepMarker(entry: JourneyEntry): ReactNode {
  if (entry.kind === 'start') return <MoStartIcon className="size-3" />;
  if (entry.kind === 'end') return <MoEndIcon className="size-3" />;
  return entry.label;
}

function getStepNodeStyle(stepColor: string, isFilled: boolean, isActive: boolean): CSSProperties {
  return {
    background: isFilled ? stepColor : undefined,
    color: isFilled ? undefined : stepColor,
    borderColor: stepColor,
    boxShadow: isActive
      ? `0 0 0 ${ACTIVE_STEP_HALO_PX}px color-mix(in srgb, ${stepColor} ${ACTIVE_STEP_HALO_MIX_PERCENT}%, transparent)`
      : undefined,
  };
}

interface MoJourneyStepperProps {
  entries: JourneyEntry[];
  activeKey: string | null;
  onStepSelect: (waypointKey: string) => void;
  onExit: () => void;
  canReturnToStartPicker: boolean;
  className?: string;
}

export function MoJourneyStepper({
  entries,
  activeKey,
  onStepSelect,
  onExit,
  canReturnToStartPicker,
  className,
}: MoJourneyStepperProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const activeIndex = entries.findIndex((entry) => entry.key === activeKey);
  const hasActiveStep = activeIndex !== -1;
  const exitLabel = canReturnToStartPicker ? 'All start points' : 'Exit journey';

  useEffect(() => {
    const track = trackRef.current;
    if (!activeKey || !track) return;
    const activeStep = track.querySelector<HTMLElement>(`[${STEP_KEY_ATTRIBUTE}="${activeKey}"]`);
    if (activeStep) centerStepInTrack(track, activeStep);
  }, [activeKey]);

  function handleStepBy(offset: number) {
    const nextEntry = hasActiveStep ? entries[activeIndex + offset] : entries[0];
    if (nextEntry) onStepSelect(nextEntry.key);
  }

  return (
    <div
      role="group"
      aria-label="Day journey"
      className={cn(
        'flex max-w-[calc(100%-4.5rem)] items-center rounded-full border bg-card/95 p-1 shadow-md backdrop-blur-sm',
        className,
      )}
    >
      <Button
        variant="ghost"
        size="icon"
        onClick={onExit}
        aria-label={exitLabel}
        title={exitLabel}
        className="size-7 shrink-0 rounded-full text-muted-foreground max-lg:size-10"
      >
        <Undo2 className="size-4" aria-hidden />
      </Button>
      <span className="mx-0.5 h-5 w-px shrink-0 bg-border" aria-hidden />
      <Button
        variant="ghost"
        size="icon"
        onClick={() => handleStepBy(-1)}
        disabled={activeIndex <= 0}
        aria-label="Previous stop"
        className="size-7 shrink-0 rounded-full text-muted-foreground max-lg:size-10"
      >
        <ChevronLeft className="size-4" aria-hidden />
      </Button>

      <div
        ref={trackRef}
        className="flex min-w-0 items-center overflow-x-auto px-2.5 py-2.5 [scrollbar-width:none]"
      >
        {entries.map((entry, index) => {
          const stepColor = getStepColor(entry);
          const isActive = index === activeIndex;
          const isFilled = !hasActiveStep || index <= activeIndex;

          return (
            <Fragment key={entry.key}>
              {index > 0 && (
                <span
                  className="h-px w-3 shrink-0 bg-border motion-safe:transition-colors motion-safe:duration-300"
                  style={isFilled ? { background: stepColor } : undefined}
                  aria-hidden
                />
              )}
              <button
                type="button"
                data-step-key={entry.key}
                onClick={() => onStepSelect(entry.key)}
                aria-label={entry.kind === 'start' ? 'Start' : `Stop ${entry.label}`}
                aria-current={isActive ? 'step' : undefined}
                title={entry.title}
                className={cn(
                  'flex size-5 shrink-0 items-center justify-center rounded-full border-[1.5px] text-3xs font-semibold tabular-nums',
                  'motion-safe:transition motion-safe:duration-300 motion-safe:ease-out',
                  isFilled ? 'text-white' : 'bg-card',
                  isActive ? 'relative z-10 scale-140' : 'lg:motion-safe:hover:scale-110',
                )}
                style={getStepNodeStyle(stepColor, isFilled, isActive)}
              >
                {renderStepMarker(entry)}
              </button>
            </Fragment>
          );
        })}
      </div>

      <Button
        variant="ghost"
        size="icon"
        onClick={() => handleStepBy(1)}
        disabled={activeIndex === entries.length - 1}
        aria-label="Next stop"
        className="size-7 shrink-0 rounded-full text-muted-foreground max-lg:size-10"
      >
        <ChevronRight className="size-4" aria-hidden />
      </Button>
    </div>
  );
}
